import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { gtag } from "@/utils/gtag";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, ArrowRight, Users, Ruler, Calendar, ChevronRight, Star, Plus } from "lucide-react";
import { SEO } from "@/components/SEO";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { ReservationDialog } from "@/components/ReservationDialog";
import { ChateauSelector } from "@/components/ChateauSelector";
import { ChateauCard } from "@/components/ChateauCard";
import { useAdminAuth } from "@/components/AdminAuth";
import { AdminPanel } from "@/components/AdminPanel";
import { useChateaux } from "@/hooks/useChateaux";
import { ChateauFormDialog } from "@/components/ChateauFormDialog";
import { AdvancedFilterControls } from "@/components/enhanced/AdvancedFilterControls";
import { useAdvancedChateauFiltering } from "@/hooks/useAdvancedChateauFiltering";
import { TrustIndicators } from "@/components/enhanced/TrustIndicators";
import { EnhancedSEOOptimizer } from "@/utils/enhancedSeoOptimizer";
export const Chateaux = () => {
  const [reservationDialogOpen, setReservationDialogOpen] = useState(false);
  const [selectedChateau, setSelectedChateau] = useState<string>("");
  const [adminPanelOpen, setAdminPanelOpen] = useState(false);
  const {
    isAdminLoggedIn
  } = useAdminAuth();
  const {
    chateaux,
    loading,
    createChateau
  } = useChateaux();

  // Enhanced filtering system
  const {
    criteria,
    setCriteria,
    filteredChateaux,
    resetFilters,
    hasActiveFilters,
    resultsCount,
    spaceOptions,
    budgetOptions,
    ageOptions,
    themeOptions,
    sizeOptions,
    featureOptions
  } = useAdvancedChateauFiltering({
    chateaux
  });

  // Enhanced SEO data
  const seoData = EnhancedSEOOptimizer.generatePageSEO('listing', {
    count: resultsCount,
    images: []
  });

  // Track page view and products for Google Ads
  useEffect(() => {
    // Track page view
    gtag('event', 'page_view', {
      page_title: 'Châteaux Gonflables - Château de Clémentine',
      page_location: window.location.href,
      send_to: 'AW-1049756763'
    });

    // Track view_item_list for remarketing
    if (chateaux && chateaux.length > 0) {
      gtag('event', 'view_item_list', {
        item_list_id: 'chateaux_gonflables',
        item_list_name: 'Châteaux Gonflables',
        items: chateaux.map((chateau, index) => ({
          item_id: chateau.id,
          item_name: chateau.nom,
          item_category: 'Château Gonflable',
          price: parseInt(chateau.prix) || 60,
          index: index
        }))
      });
    }
  }, [chateaux]);
  const handleCreateChateau = async (chateauData: any) => {
    await createChateau(chateauData);
  };
  const handleReservation = (chateauId: string) => {
    setSelectedChateau(chateauId);
    setReservationDialogOpen(true);
  };
  return <>
      <SEO title={seoData.title} description={seoData.description} keywords={seoData.keywords.join(', ')} canonicalUrl={seoData.canonicalUrl} ogImage={seoData.ogImage} schema={seoData.schema} />
      <Header />
      <main className="min-h-screen bg-gradient-sky">
      {/* Hero Section */}
      <section className="py-16 text-center">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl md:text-6xl font-display font-bold mb-6">
            <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
              Découvrez Tous Nos Châteaux
            </span>
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed mb-8">
            Une collection complète de châteaux gonflables pour tous les goûts et tous les âges. 
            Trouvez le château parfait pour votre événement !
          </p>
          <div className="flex flex-wrap gap-4 justify-center text-sm">
            <div className="flex items-center bg-white/90 rounded-full px-4 py-2">
              <Star className="w-4 h-4 mr-2 text-accent" />
              <span className="font-medium">À partir de 30€</span>
            </div>
            <div className="flex items-center bg-white/90 rounded-full px-4 py-2">
              <Calendar className="w-4 h-4 mr-2 text-primary" />
              <span className="font-medium">Week-end complet</span>
            </div>
            <div className="flex items-center bg-white/90 rounded-full px-4 py-2">
              <Ruler className="w-4 h-4 mr-2 text-secondary" />
              <span className="font-medium">Différentes tailles</span>
            </div>
          </div>
          
          {/* Admin Add Product Button */}
          {isAdminLoggedIn && <div className="mt-8 flex gap-4 justify-center">
              <ChateauFormDialog onSave={handleCreateChateau} trigger={<Button variant="hero" size="lg">
                    <Plus className="w-4 h-4 mr-2" />
                    Ajouter un Château
                  </Button>} title="Ajouter un Nouveau Château" />
              <Button variant="outline" size="lg" onClick={() => setAdminPanelOpen(true)}>
                Panneau Admin
              </Button>
            </div>}
        </div>
      </section>

      {/* Advanced Filters Section */}
      <section className="pb-8">
        <div className="container mx-auto px-4">
          <AdvancedFilterControls criteria={criteria} onCriteriaChange={setCriteria} spaceOptions={spaceOptions} budgetOptions={budgetOptions} ageOptions={ageOptions} themeOptions={themeOptions} sizeOptions={sizeOptions} featureOptions={featureOptions} onReset={resetFilters} hasActiveFilters={hasActiveFilters} resultsCount={resultsCount} />
        </div>
      </section>

      {/* Trust Indicators */}
      

      {/* Châteaux Grid */}
      <section className="pb-20">
        <div className="container mx-auto px-4">
          {loading ? <div className="text-center py-12">
              <p className="text-muted-foreground">Chargement des châteaux...</p>
            </div> : <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredChateaux.map(chateau => <ChateauCard key={chateau.id} chateau={{
              ...chateau,
              ageRecommande: chateau.age_recommande
            }} onReservation={handleReservation} />)}
            </div>}
          
          {!loading && filteredChateaux.length === 0 && <div className="text-center py-12">
              <p className="text-muted-foreground text-lg">
                Aucun château ne correspond à vos critères.
              </p>
              <Button variant="outline" onClick={resetFilters} className="mt-4">
                Réinitialiser les filtres
              </Button>
            </div>}
        </div>
      </section>

      {/* Help Choosing Form */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <ChateauSelector />
          </div>
        </div>
      </section>
      </main>
      <Footer />
      
      <ReservationDialog open={reservationDialogOpen} onOpenChange={setReservationDialogOpen} preSelectedChateau={selectedChateau} />
      
      <AdminPanel isOpen={adminPanelOpen} onClose={() => setAdminPanelOpen(false)} />
    </>;
};